import { mapGetters, mapState } from "vuex";

import CentraProduct from "../centra/frontend/mixins/product";
import CentraCart from "../centra/frontend/mixins/cart.js";
import * as imgproxy from "@made-people/imgproxy";

export default {
  name: "Product",
  mixins: [CentraProduct, CentraCart],
  props: {
    // These could safely be removed or what?
    marketId: {
      type: String,
      required: false,
    },
    pricelistId: {
      type: String,
      required: false,
    },
    languageCode: {
      type: String,
      required: true,
    },
    id: {
      type: String,
      required: true,
    },
    url: {
      type: String,
      required: true,
    },
  },
  data() {
    return {
      instagramImageLoaded: true, // change to false when we enable the instagramImage
      meta: {
        title: "",
        meta: [],
        canonical: "",
      },
    };
  },
  computed: {
    ...mapGetters({
      getCollectionById: "storyblok/getCollectionById",
      hasProductID: "wishlist/hasProductID",
      settings: "storyblok/settings",
      getCurrencyCodeByCountryCode: 'frontend/getCurrencyCodeByCountryCode',
      currentCountryCode: 'frontend/currentCountryCode',
      currentLanguageCode: 'frontend/currentLanguageCode'
    }),
    ...mapState({
      notifyMeEnabled: (state) => state.ui.notifyMeEnabled,
    }),
    collectionContent() {
      if (this.product) {
        return this.getCollectionById(this.product.collection)
          ? this.getCollectionById(this.product.collection).content
          : [];
      }
      return [];
    },
    /**
     * In case there is more than one badge in Centra
     */
    badge() {
      if (this.product.badge && Object.values(this.product.badge).length) {
        return {
          type: Object.values(this.product.badge)[0].toLowerCase(),
          label: Object.values(this.product.badge)[0].split("-").join(" "),
        };
      }
      return null;
    },
    isInWishlist() {
      return this.hasProductID(this.product.product);
    },
    enableMobileNotifyMe() {
      return (
        this.notifyMeEnabled && this.product.isOnesize && !this.product.inStock
      );
    },
    images() {
      return this.product.media.filter(
        (x) => !/-(feed|hover|collection)-/.test(x)
      );
    },
    jsonLd() {
      return this.getJsonLd();
    },
  },
  async mounted() {
    if (!this.product) {
      await this.fetchProduct();
    } else {
      this.setMeta();
    }
    if (!this.collectionContent.length) {
      this.fetchCollectionContent();
    }
    this.gtm_trackPage("product", this.meta.title);
    this.gtm_trackProductDetail(this.product);
    this.findify_trackPageView();
  },
  created() {
    if (this.product) {
      this.setMeta();
      this.fetchCollectionContent();
    } else {
      this.postLoadCentraProduct(this.setMeta);
      this.postLoadCentraProduct(this.fetchCollectionContent);
    }
  },
  serverPrefetch() {
    return this.fetchProduct();
  },
  methods: {
    getJsonLd() {
      try {
        const product = this.product;
        const reviews = this.reviews;
        const aggregateRating = {};
        const reviewCount = reviews?.bottomline?.total_review || 0;
        if (reviewCount) {
          const ratingValueDec = Math.round(
            reviews.bottomline.average_score * 10
          );
          let ratingValue = ratingValueDec / 10;
          if (ratingValueDec % 10 === 0) {
            // Even rating values should not have decimals at all
            ratingValue = Math.abs(ratingValue);
          }
          aggregateRating.aggregateRating = {
            "@type": "AggregateRating",
            ratingValue,
            reviewCount: reviews.bottomline.total_review,
          };
        }
        if (product) {
          const availability = product.inStock
            ? "https://schema.org/InStock"
            : "https://schema.org/OutOfStock";
          const currency =
            this.$store.getters["frontend/pricelist"].currency.currency;
          return {
            "@context": "https://schema.org/",
            "@type": "Product",
            name: product.name,
            color: product.silkVariantName,
            material: product.google_merchant_material,
            image: [
              this.getImageUrl(
                product.mainImage,
                "extend:1/resize:fit/width:1080/quality:70"
              ),
            ],
            description: product.description,
            sku: product.sku,
            brand: {
              "@type": "Brand",
              name: this.$config.site.name,
              url: this.$config.baseUrl,
            },
            offers: {
              "@type": "Offer",
              availability,
              price: product.priceAsNumber,
              priceCurrency: currency,
              url: this.$config.baseUrl + this.$u(product.url),
              itemCondition: "NewCondition",
            },
            ...aggregateRating,
          };
        }
      } catch (e) {
        console.error("Failed to generate product ld+json", e);
      }
      return null;
    },
    getImageUrl(image, def) {
      imgproxy.useBaseUrl(this.$config.imageProxyUrl);
      return imgproxy.transform(image, def);
    },
    toggleWishlist() {
      if (this.isInWishlist) {
        this.removeFromWishlist();
      } else {
        this.addToWishlist();
      }
    },
    mobileNotifyMe() {
      this.showProductRestockSidebar(Object.keys(this.product.items)[0]);
    },
    showProductRestockSidebar(id) {
      if (id) {
        this.$store.dispatch("ui/showProductRestockSidebar", id);
      }
    },
    removeFromWishlist() {
      this.$store.dispatch("wishlist/removeProductID", this.product.product);
    },
    addToWishlist() {
      this.$store.dispatch("wishlist/addProductID", this.product.product);
    },
    fetchCollectionContent() {
      if (this.$config.useCollectionContent) {
        return this.$store.dispatch(
          "storyblok/fetchCollectionContent",
          this.product.collection
        );
      }
    },
    findify_trackPageView() {
      this.$findifyAnalytics().sendEvent("view-page", {
        url: window.document.URL,
        ref: window.document.referrer,
        width: window.innerWidth,
        height: window.innerHeight,
        variant_item_id: Object.keys(this.product.items)[0],
        item_id: this.product.product,
      });
    },
    setMeta() {
      const metaTitle = this.product.meta.title || this.product.name;

      // Remove tags and replace newlines with '. '
      const metaDescription = (
        this.product.meta.description ||
        this.product.description ||
        this.product.excerpt ||
        this.product.shortDescription ||
        (this.settings?.default_meta_description ?? "")
      )
        .replace(/(<([^>]+)>)/gi, "")
        .replace(/\r?\n|\r/g, ". ");
      const canonical = this.$config.baseUrl + this.$u(this.product.url);

      const ogImage =
        this.product.thumbnailImage ||
        this.settings?.openGraphImage?.filename ||
        null;

      const currency = this.getCurrencyCodeByCountryCode(this.currentCountryCode)
      const availability = this.product.comingSoon ? 'out of stock' : this.product.inStock ? 'in stock' : 'out of stock'
      const meta = [
        {
          hid: 'description',
          name: 'description',
          content: metaDescription,
        },
        {
          hid: 'og:title',
          property: 'og:title',
          content: metaTitle,
        },
        {
          hid: 'og:description',
          property: 'og:description',
          content: metaDescription,
        },
        {
          hid: 'og:url',
          property: 'og:url',
          content: canonical,
        },
        {
          hid: 'og:type',
          property: 'og:type',
          content: 'website',
        },
        {
          hid: 'og:site_name',
          property: 'og:site_name',
          content: this.$config.site.name,
        },
        {
          hid: 'og:locale',
          property: 'og:locale',
          content: this.currentLanguageCode,
        },
        {
          hid: 'og:price:amount',
          property: 'og:price:amount',
          content: this.product.priceBeforeDiscountAsNumber,
        },
        {
          hid: 'og:price:currency',
          property: 'og:price:currency',
          content: currency,
        },
        {
          hid: 'product:availability',
          property: 'product:availability',
          content: availability,
        },
        {
          hid: 'product:price:amount',
          property: 'product:price:amount',
          content: this.product.priceBeforeDiscountAsNumber,
        },
        {
          hid: 'product:price:currency',
          property: 'product:price:currency',
          content: currency,
        },
        {
          hid: 'product:retailer_item_id',
          property: 'product:retailer_item_id',
          content: this.product.productSku,
        },
        {
          hid: 'product:category',
          property: 'product:category',
          content: this.product.googleMerchantCategory,
        },
      ]
      if (this.product.priceAsNumber !== this.product.priceBeforeDiscountAsNumber) {
        meta.push({
          hid: 'product:sale_price:amount',
          property: 'product:sale_price:amount',
          content: this.product.priceAsNumber,
        })
        meta.push({
          hid: 'product:sale_price:currency',
          property: 'product:sale_price:currency',
          content: currency,
        })
      }

      /**
       * The image is portrait and have to fit into landscape without heads falling off.
       */
      if (ogImage) {
        meta.push({
          hid: "og:image",
          content: this.getImageUrl(
            ogImage,
            "extend:1/resize:fit/width:1200/height:630/quality:70"
          ),
          property: "og:image",
        });
      }
      this.meta.title =
        (this.settings?.pdp_meta_title_prefix ?? "") +
        metaTitle +
        (this.settings?.pdp_meta_title_suffix ?? "");
      this.meta.meta = meta;
      this.meta.canonical = canonical;

      // ???
      return new Promise((resolve, reject) => {
        resolve(true);
      });
    },
    getSizeChartDelimiter() {
      return this.$config.sizeChartDelimiter
        ? this.$config.sizeChartDelimiter
        : sizeChartDelimiter;
    },
  },
  head() {
    const output = {
      title: this.meta.title,
      meta: this.meta.meta,
      link: [
        {
          rel: "canonical",
          href: this.meta.canonical,
        },
      ],
    };
    return output;
  },
};
